import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { BOOKING_STATUSES } from "../../data/content";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { MapPin, Monitor, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useSite } from "../../context/SiteContext";

const STATUS_COLORS = {
  REQUESTED: "bg-flame/15 text-flame border-flame/30",
  APPROVED: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  RESCHEDULED: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  CANCELLED: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function Bookings() {
  const { settings } = useSite();
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [active, setActive] = useState(null);

  const load = () => {
    const q = status === "ALL" ? "" : `?status=${status}`;
    api.get(`/admin/rush-hour-bookings${q}`).then((res) => setBookings(res.data)).catch(() => toast.error("Could not load bookings"));
  };
  useEffect(load, [status]);

  const update = async (bookingId, payload) => {
    await api.patch(`/admin/rush-hour-bookings/${bookingId}`, payload).catch(() => toast.error("Update failed"));
    const q = status === "ALL" ? "" : `?status=${status}`;
    const res = await api.get(`/admin/rush-hour-bookings${q}`);
    setBookings(res.data);
    if (active?.booking_id === bookingId) setActive(res.data.find((b) => b.booking_id === bookingId) || null);
  };

  const remove = async (bookingId) => {
    await api.delete(`/admin/rush-hour-bookings/${bookingId}`).catch(() => toast.error("Delete failed"));
    setActive(null);
    load();
    toast.success("Booking removed");
  };

  const estHours = parseFloat(active?.expected_hours) || 0;
  const estRate = active?.specialist === "super" ? settings.rates.super : settings.rates.specialist;

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-white">Rush Hour Operations</h1>
          <p className="mt-2 text-sm text-slate-400">{bookings.length} session request{bookings.length === 1 ? "" : "s"}</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="bookings-status-filter" className="w-56 bg-navy-800 border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-navy-800 border-white/10">
            <SelectItem value="ALL">ALL STATUSES</SelectItem>
            {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Client</TableHead>
              <TableHead className="text-slate-400">Specialist</TableHead>
              <TableHead className="text-slate-400 hidden md:table-cell">Date / Time</TableHead>
              <TableHead className="text-slate-400 hidden lg:table-cell">Hours</TableHead>
              <TableHead className="text-slate-400 hidden lg:table-cell">Mode</TableHead>
              <TableHead className="text-slate-400 hidden sm:table-cell">Assigned</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((b) => (
              <TableRow key={b.booking_id} data-testid={`booking-row-${b.booking_id}`} onClick={() => setActive(b)} className="border-white/5 cursor-pointer hover:bg-white/5">
                <TableCell className="font-semibold text-white">
                  {b.name}
                  {b.is_demo && <Badge className="ml-2 bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[8px]">DEMO</Badge>}
                  <span className="block text-[11px] font-normal text-slate-500">{b.company}</span>
                </TableCell>
                <TableCell>
                  <Badge className={`font-mono text-[9px] ${b.specialist === "super" ? "bg-flame/15 text-flame border-flame/30" : "bg-white/10 text-slate-300 border-white/10"}`}>
                    {b.specialist === "super" ? "SUPER VIBE" : "SPECIALIST"}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-400 hidden md:table-cell">{b.preferred_date || "—"} {b.preferred_start_time && `· ${b.preferred_start_time}`}</TableCell>
                <TableCell className="text-slate-400 hidden lg:table-cell">{b.expected_hours || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                    {b.mode === "onsite" ? <MapPin className="w-3.5 h-3.5 text-flame" /> : <Monitor className="w-3.5 h-3.5 text-sky-400" />}
                    {b.mode?.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="text-slate-400 text-xs hidden sm:table-cell">{b.assigned_specialist || "—"}</TableCell>
                <TableCell><Badge className={`font-mono text-[9px] ${STATUS_COLORS[b.status]}`}>{b.status}</Badge></TableCell>
              </TableRow>
            ))}
            {!bookings.length && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">No sessions in this view.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
          {active && (
            <div data-testid="booking-detail-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white flex items-center gap-3">
                  {active.name} — {active.company || "Independent"}
                  {active.is_demo && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[9px]">DEMO</Badge>}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-mono">
                {[["EMAIL", active.email], ["PHONE", active.phone || "—"], ["TYPE", active.application_type || "—"],
                  ["DATE", active.preferred_date || "—"], ["START", active.preferred_start_time || "—"], ["HOURS", active.expected_hours || "—"],
                  ["MODE", active.mode?.toUpperCase()], ["SPECIALIST", active.specialist === "super" ? "SUPER VIBE CODER" : "VIBE CODING SPECIALIST"],
                  ["ILLUSTRATIVE COST", estHours ? inr(estHours * estRate) : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-navy-900/70 p-3">
                    <p className="text-[9px] tracking-widest text-slate-500">{k}</p>
                    <p className="mt-1 text-slate-200 break-words">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-4 text-sm">
                {[["REQUIREMENT", active.requirement], ["KEY FEATURES", active.expected_features]].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}>
                    <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-1">{k}</p>
                    <p className="text-slate-300 leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">STATUS</p>
                  <Select value={active.status} onValueChange={(v) => update(active.booking_id, { status: v })}>
                    <SelectTrigger data-testid="booking-status-select" className="bg-navy-900/60 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">ASSIGN SPECIALIST</p>
                  <Input defaultValue={active.assigned_specialist} data-testid="booking-assign-input" placeholder="Specialist name" onBlur={(e) => update(active.booking_id, { assigned_specialist: e.target.value })} className="bg-navy-900/60 border-white/10" />
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">RESCHEDULE DATE</p>
                  <Input type="date" defaultValue={active.preferred_date} data-testid="booking-date-input" onBlur={(e) => e.target.value && update(active.booking_id, { preferred_date: e.target.value, status: "RESCHEDULED" })} className="bg-navy-900/60 border-white/10" />
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">START TIME</p>
                  <Input type="time" defaultValue={active.preferred_start_time} data-testid="booking-time-input" onBlur={(e) => e.target.value && update(active.booking_id, { preferred_start_time: e.target.value })} className="bg-navy-900/60 border-white/10" />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 justify-between">
                <div className="flex gap-3">
                  <Button onClick={() => update(active.booking_id, { status: "APPROVED" })} data-testid="booking-approve-btn" className="bg-sky-600 hover:bg-sky-500 text-white">Approve</Button>
                  <Button onClick={() => update(active.booking_id, { status: "COMPLETED" })} data-testid="booking-complete-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white">Mark completed</Button>
                </div>
                <Button variant="outline" onClick={() => remove(active.booking_id)} data-testid="booking-delete-btn" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
